import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Logo from "../components/Logo";
import { useCustomer } from "../context/CustomerContext";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const Login = () => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { user, login, register } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) navigate(location.state?.from || "/", { replace: true });
  }, [user]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back" : "Welcome to Jwellary");
      navigate(location.state?.from || "/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  return (
    <main className="pt-[68px] min-h-screen flex items-center justify-center px-5 py-16" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-2xl gold-hairline bg-[#101013] p-8 md:p-10"
      >
        <div className="flex justify-center mb-6">
          <Logo size={38} wordClass="text-2xl" />
        </div>

        <div className="flex rounded-full border border-[#d4af37]/25 p-1 mb-8" data-testid="login-mode-tabs">
          {[
            { id: "login", label: "Sign In" },
            { id: "register", label: "Create Account" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setMode(t.id)}
              data-testid={`login-tab-${t.id}`}
              className={`flex-1 py-2.5 rounded-full text-xs tracking-[0.14em] uppercase transition-all duration-300 ${
                mode === t.id ? "bg-[#d4af37] text-[#0a0a0b] font-semibold" : "text-zinc-400 hover:text-[#f3e5ab]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <p className="text-center text-sm text-zinc-500 mb-8">
          {mode === "login"
            ? "Welcome back to the Golden Circle."
            : "Sign up for faster checkout and first access to new pieces."}
        </p>

        <form onSubmit={submit} className="space-y-5">
          {mode === "register" && (
            <div>
              <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Full Name</label>
              <input required value={form.name} onChange={f("name")} className="input-dark" data-testid="login-name-input" placeholder="Aishwarya Rai" />
            </div>
          )}
          <div>
            <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Email</label>
            <input required type="email" value={form.email} onChange={f("email")} className="input-dark" data-testid="login-email-input" placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Password</label>
            <input
              required
              type="password"
              value={form.password}
              onChange={f("password")}
              className="input-dark"
              data-testid="login-password-input"
              placeholder={mode === "register" ? "Minimum 8 characters" : "••••••••"}
              minLength={mode === "register" ? 8 : undefined}
            />
          </div>
          <button type="submit" disabled={busy} className="btn-gold w-full !py-4" data-testid="login-submit-button">
            {busy ? "Please wait..." : mode === "login" ? "Sign In" : "Create My Account"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-zinc-600">
          By continuing you agree to Jwellary's terms. Your details are used only to fulfil orders.
        </p>
        <p className="mt-4 text-center">
          <Link to="/shop" className="text-xs tracking-[0.14em] uppercase text-[#d4af37]/80 hover:text-[#f3e5ab] transition-colors" data-testid="login-continue-shopping-link">
            Continue shopping →
          </Link>
        </p>
      </motion.div>
    </main>
  );
};

export default Login;
